import { useState } from 'react';
import { clsx } from 'clsx';
import { Check, Copy } from 'lucide-react';
import { IconButton } from '@/components/ds-zta';
import { copyWithToast } from '@/lib/copy';

// CodeBlock renders a copyable shell/YAML snippet on the recessed code well:
// a slim header bar (language label + Copy) over the code. When `embedded` it
// drops the outer frame and margin so it can sit flush inside a tabbed
// container whose tab strip already acts as the header's top edge. Shared by
// Setup Guide and Settings › Integrations (design evaluation F41).
export default function CodeBlock({ code, lang = 'bash', embedded = false }: { code: string; lang?: string; embedded?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!(await copyWithToast(code, { success: false }))) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div
      className={clsx(
        'overflow-hidden bg-[var(--d2d-bg)]',
        embedded ? '' : 'my-3 rounded-lg border border-[var(--d2d-border)]'
      )}
    >
      <div className="flex items-center justify-between gap-2 border-b border-[var(--d2d-border)] bg-[var(--ds-color-bg-subtle)] py-1 pl-3 pr-1">
        <span className="text-label font-medium uppercase text-[var(--d2d-text-muted)]">{lang}</span>
        <IconButton
          size="sm"
          onClick={copy}
          aria-label={copied ? 'Copied' : 'Copy to clipboard'}
          tooltip={copied ? 'Copied' : 'Copy to clipboard'}
          icon={copied ? <Check className="h-3.5 w-3.5 text-[var(--d2d-success)]" /> : <Copy className="h-3.5 w-3.5" />}
        />
      </div>
      <pre className="overflow-x-auto p-4 text-[13px] leading-relaxed text-[var(--d2d-text-primary)]">
        <code>{code}</code>
      </pre>
      <span className="sr-only" aria-live="polite">{copied ? 'Copied to clipboard' : ''}</span>
    </div>
  );
}
